/**
 * Camera Coordinate Transforms
 * L4-ZOOM-002: Pure functions for coordinate transformation
 */

import {
  Point,
  CameraState,
  CameraConfig,
  clamp,
} from './camera-types';

/**
 * Convert screen coordinates to world coordinates
 *
 * Screen coordinates are what the user sees (pixel position on canvas)
 * World coordinates are the "true" position in the game world
 *
 * Formula: world = (screen - pan) / zoom
 *
 * @param screenPos - Position in screen/canvas coordinates
 * @param cameraState - Current camera zoom and pan
 * @returns Position in world coordinates
 */
export function screenToWorld(screenPos: Point, cameraState: CameraState): Point {
  return {
    x: (screenPos.x - cameraState.panX) / cameraState.zoom,
    y: (screenPos.y - cameraState.panY) / cameraState.zoom,
  };
}

/**
 * Convert world coordinates to screen coordinates
 *
 * Inverse of screenToWorld
 *
 * Formula: screen = world * zoom + pan
 *
 * @param worldPos - Position in world coordinates
 * @param cameraState - Current camera zoom and pan
 * @returns Position in screen/canvas coordinates
 */
export function worldToScreen(worldPos: Point, cameraState: CameraState): Point {
  return {
    x: worldPos.x * cameraState.zoom + cameraState.panX,
    y: worldPos.y * cameraState.zoom + cameraState.panY,
  };
}

/**
 * Calculate new camera state for zoom centered on a screen point
 *
 * This implements "zoom toward cursor" behavior:
 * 1. Find the world position under the cursor
 * 2. Apply new zoom
 * 3. Adjust pan so that same world position is still under cursor
 *
 * @param currentState - Current camera state
 * @param targetZoom - Desired zoom level (will be clamped)
 * @param screenAnchor - Screen position to zoom toward (cursor position)
 * @param config - Camera config with zoom limits
 * @returns New camera state with updated zoom and pan
 */
export function calculateZoomAtPoint(
  currentState: CameraState,
  targetZoom: number,
  screenAnchor: Point,
  config: CameraConfig
): CameraState {
  // Clamp target zoom to valid range
  const newZoom = clamp(targetZoom, config.minZoom, config.maxZoom);

  // If zoom didn't change, return unchanged state
  if (newZoom === currentState.zoom) {
    return currentState;
  }

  // Find world position under the anchor point
  const worldAnchor = screenToWorld(screenAnchor, currentState);

  // Calculate new pan so worldAnchor maps back to screenAnchor at new zoom
  // screenAnchor = worldAnchor * newZoom + newPan
  // newPan = screenAnchor - worldAnchor * newZoom
  const newPanX = screenAnchor.x - worldAnchor.x * newZoom;
  const newPanY = screenAnchor.y - worldAnchor.y * newZoom;

  return {
    zoom: newZoom,
    panX: newPanX,
    panY: newPanY,
  };
}

/**
 * Calculate zoom change from wheel delta
 *
 * Converts raw wheel delta to a zoom multiplier
 *
 * @param wheelDelta - Raw wheel event deltaY
 * @param currentZoom - Current zoom level
 * @param wheelZoomFactor - Sensitivity factor from config
 * @returns New target zoom level
 */
export function calculateWheelZoom(
  wheelDelta: number,
  currentZoom: number,
  wheelZoomFactor: number
): number {
  // Negative delta = scroll up = zoom in
  // Positive delta = scroll down = zoom out
  // Use multiplicative zoom for smooth feel
  const zoomMultiplier = 1 - wheelDelta * wheelZoomFactor;
  return currentZoom * zoomMultiplier;
}
